import { Table, Text } from '@lmc/ui-kit'
import useConfig from 'hooks/useConfig'
import { NAMESPACE } from 'locale'
import { painchekConstants } from 'locale/painchek'
import { capitalize } from 'lodash'
import type React from 'react'
import { useTranslation } from 'react-i18next'

interface Assessment {
  uuid?: string
  patient?: string
  json_version?: string
  data?: {
    assessment?: {
      date?: string
      mode?: string
      pain_score?: number
      pain_level?: string
      is_editable?: boolean
      timing?: string
      comment?: string
      body_location?: string
    }
    device?: {
      manufacturer?: string
      model?: string
      os_type?: string
      os_version?: string
      app_version?: string
    }
    face?: {
      mode?: string
      initial_mode?: string
      camera?: string
      capture_seconds?: number
      frames?: number
      frame_errors?: number
      comment?: string
      score?: number
      descriptors?: {
        brow_lowering?: boolean
        cheek_raising?: boolean
        tightening_eyelids?: boolean
        wrinkling_nose?: boolean
        raising_upper_lip?: boolean
        pulling_corner_lip?: boolean
        mouth_stretch?: boolean
        parting_lips?: boolean
        closing_eyes?: boolean
      }
    }
    voice?: {
      voice_recording?: string
      comment?: string
      score?: number
      descriptors?: {
        noisy_sounds?: boolean
        requesting_help?: boolean
        groaning?: boolean
        moaning?: boolean
        crying?: boolean
        screaming?: boolean
        loudtalk?: boolean
        howling?: boolean
        sighing?: boolean
      }
    }
    movement?: {
      comment?: string
      score?: number
      descriptors?: {
        altered_random_movement?: boolean
        restlessness?: boolean
        freezing?: boolean
        guarding_touching?: boolean
        moving_away?: boolean
        abnormal_movement?: boolean
        pacing_wandering?: boolean
      }
    }
    behaviour?: {
      comment?: string
      score?: number
      descriptors?: {
        introvert?: boolean
        verbally_offensive?: boolean
        aggressive?: boolean
        fear_or_dislike?: boolean
        inappropriate_behaviour?: boolean
        confused?: boolean
        distressed?: boolean
      }
    }
    activity?: {
      comment?: string
      score?: number
      descriptors?: {
        resisting_care?: boolean
        prolonged_resting?: boolean
        altered_sleep?: boolean
        altered_routine?: boolean
      }
    }
    body?: {
      comment?: string
      score?: number
      descriptors?: {
        profuse_sweating?: boolean
        pale_flushed?: boolean
        feverish_cold?: boolean
        rapid_breathing?: boolean
        painful_injuries?: boolean
        painful_conditions?: boolean
      }
    }
  }
}

interface Props {
  assessmentResult: Assessment
}

const styles = {
  subTitle: { paddingTop: 42 },
  container: { paddingBottom: 42 },
  link: { fontSize: 14, paddingBottom: 24 },
  table: { paddingBottom: 42 }
}

const toLabel = (val: boolean | undefined) => (val ? 'Present' : '-')

export const PainChekAssessment = ({
  assessmentResult
}: Props): React.ReactElement => {
  const { t } = useTranslation(NAMESPACE.PAINCHECK)

  const { config } = useConfig()

  if (!assessmentResult.data)
    return <Text as="p2">{t(painchekConstants.EMPTY_STATE)}</Text>
  const { face, assessment, voice, movement, body, activity, behaviour } =
    assessmentResult.data
  return (
    <div style={styles.container}>
      <Text as="h4">{t(painchekConstants.ASSESSMENT_DETAILS)}</Text>
      <div style={styles.link}>
        <a
          href={`${config?.PAINCHEK_URL}/assessments/${assessmentResult.uuid}`}
          target="_blank"
          rel="noreferrer"
        >
          {t(painchekConstants.VIEW_ASSESSMENT)}
        </a>
      </div>
      <Table
        style={styles.table}
        pagination={false}
        columns={[
          { dataIndex: 'key', title: 'Assessment', width: 220 },
          { dataIndex: 'value', title: ' ' }
        ]}
        dataSource={[
          {
            key: 'Assessment Mode',
            value:
              assessment?.mode === 'painchek'
                ? 'PainChek'
                : capitalize(assessment?.mode)
          },
          { key: 'Pain Score', value: assessment?.pain_score },
          { key: 'Pain Level', value: capitalize(assessment?.pain_level) },
          { key: 'Assessment Timing', value: capitalize(assessment?.timing) },
          { key: 'Comment', value: assessment?.comment }
        ]}
      />
      <Table
        style={styles.table}
        pagination={false}
        columns={[
          { dataIndex: 'key', title: 'Face', width: 220 },
          { dataIndex: 'value', title: ' ' }
        ]}
        dataSource={[
          { key: 'Score', value: face?.score },
          { key: 'Mode', value: capitalize(face?.mode) },
          {
            key: 'Brow Lowering',
            value: toLabel(face?.descriptors?.brow_lowering)
          },
          {
            key: 'Cheek Raising',
            value: toLabel(face?.descriptors?.cheek_raising)
          },
          {
            key: 'Closing Eyes',
            value: toLabel(face?.descriptors?.closing_eyes)
          },
          {
            key: 'Mouth Streth',
            value: toLabel(face?.descriptors?.mouth_stretch)
          },
          {
            key: 'Parting Lips',
            value: toLabel(face?.descriptors?.parting_lips)
          },
          {
            key: 'Pulling Corner Lip',
            value: toLabel(face?.descriptors?.pulling_corner_lip)
          },
          {
            key: 'Raising Upper Lip',
            value: toLabel(face?.descriptors?.raising_upper_lip)
          },
          {
            key: 'Tightening Eyelids',
            value: toLabel(face?.descriptors?.tightening_eyelids)
          },
          {
            key: 'Wrinkling Nose',
            value: toLabel(face?.descriptors?.wrinkling_nose)
          },
          { key: 'Comment', value: face?.comment }
        ]}
      />
      <Table
        style={styles.table}
        pagination={false}
        columns={[
          { dataIndex: 'key', title: 'Voice', width: 220 },
          { dataIndex: 'value', title: ' ' }
        ]}
        dataSource={[
          { key: 'Score', value: voice?.score },
          { key: 'Crying', value: toLabel(voice?.descriptors?.crying) },
          { key: 'Groaning', value: toLabel(voice?.descriptors?.groaning) },
          { key: 'Howling', value: toLabel(voice?.descriptors?.howling) },
          { key: 'Loudtalk', value: toLabel(voice?.descriptors?.loudtalk) },
          { key: 'Moaning', value: toLabel(voice?.descriptors?.moaning) },
          {
            key: 'Noisy Sounds',
            value: toLabel(voice?.descriptors?.noisy_sounds)
          },
          {
            key: 'Requesting Help',
            value: toLabel(voice?.descriptors?.requesting_help)
          },
          { key: 'Screaming', value: toLabel(voice?.descriptors?.screaming) },
          { key: 'Sighing', value: toLabel(voice?.descriptors?.sighing) },
          { key: 'Comment', value: voice?.comment }
        ]}
      />
      <Table
        style={styles.table}
        pagination={false}
        columns={[
          { dataIndex: 'key', title: 'Activity', width: 220 },
          { dataIndex: 'value', title: ' ' }
        ]}
        dataSource={[
          { key: 'Score', value: activity?.score },
          {
            key: 'Altered Routine',
            value: toLabel(activity?.descriptors?.altered_routine)
          },
          {
            key: 'Altered Sleeep',
            value: toLabel(activity?.descriptors?.altered_sleep)
          },
          {
            key: 'Prolonged Resting',
            value: toLabel(activity?.descriptors?.prolonged_resting)
          },
          {
            key: 'Resisting Care',
            value: toLabel(activity?.descriptors?.resisting_care)
          },
          { key: 'Comment', value: activity?.comment }
        ]}
      />
      <Table
        style={styles.table}
        pagination={false}
        columns={[
          { dataIndex: 'key', title: 'Body', width: 220 },
          { dataIndex: 'value', title: ' ' }
        ]}
        dataSource={[
          { key: 'Score', value: body?.score },
          {
            key: 'Feverish Cold',
            value: toLabel(body?.descriptors?.feverish_cold)
          },
          {
            key: 'Painful Conditions',
            value: toLabel(body?.descriptors?.painful_conditions)
          },
          {
            key: 'Painful Injuries',
            value: toLabel(body?.descriptors?.painful_injuries)
          },
          {
            key: 'Pale Flushed',
            value: toLabel(body?.descriptors?.pale_flushed)
          },
          {
            key: 'Profuse Sweating',
            value: toLabel(body?.descriptors?.profuse_sweating)
          },
          {
            key: 'Rapid Breathing',
            value: toLabel(body?.descriptors?.rapid_breathing)
          },
          { key: 'Comment', value: body?.comment }
        ]}
      />
      <Table
        style={styles.table}
        pagination={false}
        columns={[
          { dataIndex: 'key', title: 'Movement', width: 220 },
          { dataIndex: 'value', title: ' ' }
        ]}
        dataSource={[
          { key: 'Score', value: movement?.score },
          {
            key: 'Abnormal Movement',
            value: toLabel(movement?.descriptors?.abnormal_movement)
          },
          {
            key: 'Altered Random Movement',
            value: toLabel(movement?.descriptors?.altered_random_movement)
          },
          { key: 'Freezing', value: toLabel(movement?.descriptors?.freezing) },
          {
            key: 'Guarding Touching',
            value: toLabel(movement?.descriptors?.guarding_touching)
          },
          {
            key: 'Moving Away',
            value: toLabel(movement?.descriptors?.moving_away)
          },
          {
            key: 'Pacing or Wandering',
            value: toLabel(movement?.descriptors?.pacing_wandering)
          },
          {
            key: 'Restlessness',
            value: toLabel(movement?.descriptors?.restlessness)
          },
          { key: 'Comment', value: movement?.comment }
        ]}
      />
      <Table
        style={styles.table}
        pagination={false}
        columns={[
          { dataIndex: 'key', title: 'Behaviour', width: 220 },
          { dataIndex: 'value', title: ' ' }
        ]}
        dataSource={[
          { key: 'Score', value: behaviour?.score },
          {
            key: 'Aggressive',
            value: toLabel(behaviour?.descriptors?.aggressive)
          },
          { key: 'Confused', value: toLabel(behaviour?.descriptors?.confused) },
          {
            key: 'Distresseed',
            value: toLabel(behaviour?.descriptors?.distressed)
          },
          {
            key: 'Fear or Dislike',
            value: toLabel(behaviour?.descriptors?.fear_or_dislike)
          },
          {
            key: 'Inappropriate Behaviour',
            value: toLabel(behaviour?.descriptors?.inappropriate_behaviour)
          },
          {
            key: 'Introvert',
            value: toLabel(behaviour?.descriptors?.introvert)
          },
          {
            key: 'Verbally Offensive',
            value: toLabel(behaviour?.descriptors?.verbally_offensive)
          },
          { key: 'Comment', value: behaviour?.comment }
        ]}
      />
    </div>
  )
}

export default PainChekAssessment
